
import { useState, useEffect } from 'react';

interface DashboardProps {
  currentUser: {
    id: string;
    name: string;
    role: 'admin' | 'agent';
  };
}

export default function Dashboard({ currentUser }: DashboardProps) {
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!currentUser.company_id) {
        setError('Société non trouvée');
        return;
      }

      // Charger données avec company_id
      const [clientsResponse, statusesResponse, usersResponse] = await Promise.all([
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=list&company_id=${currentUser.company_id}`, {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          }
        }),
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clients-management?action=statuses&company_id=${currentUser.company_id}`, {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          }
        }),
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/users-management?action=list`, {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          }
        })
      ]);

      const clientsResult = await clientsResponse.json();
      const statusResult = await statusesResponse.json();

      const usersData = await usersResponse.json();
      if (usersData.success) {
        // Filtrer les utilisateurs par société
        const companyUsers = usersData.users.filter((user: any) => user.company_id === currentUser.company_id);
        setUsers(companyUsers);
      }

      if (clientsResult.success) {
        setClients(clientsResult.clients || []);
      }

      if (statusResult.success) {
        setStatuses(statusResult.statuses || []);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Charger les données au démarrage
    loadData();

    // Actualiser les données toutes les 60 secondes (1 minute) pour la synchronisation optimisée
    const interval = setInterval(loadData, 60000);

    return () => clearInterval(interval);
  }, []);

  // Filtrer les clients selon le rôle
  const visibleClients = currentUser.role === 'admin' 
    ? clients 
    : clients.filter(client => client.assigned_agent === currentUser.id);

  // Calculer les statistiques en temps réel
  const stats = statuses.map(status => ({
    ...status,
    count: visibleClients.filter(client => client.status === status.id).length
  }));

  const totalClients = visibleClients.length;
  const recentClients = visibleClients
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  // Calculer RDV aujourd'hui pour mobile
  const today = new Date().toISOString().split('T')[0];
  const rdvToday = visibleClients.filter(client => 
    client.status === 'rdv-fixe' && client.rdv_date === today
  );

  if (loading && clients.length === 0) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header Mobile/Desktop */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-600 text-sm sm:text-base">
          Bienvenue {currentUser.name} - Vue {currentUser.role === 'admin' ? 'administrateur' : 'agent'}
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 mt-2">
          <p className="text-sm text-gray-500">{totalClients} clients au total</p>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
            <span className="text-xs text-green-600">Sync 1 min</span>
          </div>
        </div>
      </div>

      {/* Message d'information mobile optimisé */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-start sm:items-center">
            <i className="ri-database-2-line text-green-600 mr-2 mt-0.5 sm:mt-0"></i>
            <div>
              <p className="text-green-800 text-sm font-semibold">
                🚀 Supabase parfaitement synchronisé !
              </p>
              <p className="text-green-700 text-xs">
                Toutes les données sont actualisées automatiquement chaque minute
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
              <span className="text-xs text-green-600">Sync 1 min</span>
            </div>
            <div className="text-green-600 font-mono text-xs">
              {totalClients} clients synchronisés
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques en temps réel - Mobile responsive */}
      <div className="mobile-stats-grid mb-6 sm:mb-8">
        {stats.map((stat) => (
          <div key={stat.id} className="mobile-stat-card mobile-card-hover">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-2 sm:mr-3 flex-shrink-0"
                style={{ backgroundColor: stat.color }}
              ></div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-600 truncate">{stat.label}</p>
                <div className="flex items-baseline space-x-1 sm:space-x-2">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{stat.count}</p>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-600 ml-1 hidden sm:inline">Auto</span>
                  </div>
                </div>
                {totalClients > 0 && (
                  <p className="text-xs text-gray-500">
                    {Math.round((stat.count / totalClients) * 100)}%
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Indicateurs de performance mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 sm:p-6 rounded-lg text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-blue-100 text-xs sm:text-sm">Total clients</p>
              <p className="text-2xl sm:text-3xl font-bold">{totalClients}</p>
            </div>
            <i className="ri-group-line text-2xl sm:text-3xl text-blue-200 hidden sm:block"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 sm:p-6 rounded-lg text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-green-100 text-xs sm:text-sm">Conversion</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {totalClients > 0 
                  ? Math.round((stats.find(s => s.id === 'termine')?.count || 0) / totalClients * 100)
                  : 0
                }%
              </p>
            </div>
            <i className="ri-trophy-line text-2xl sm:text-3xl text-green-200 hidden sm:block"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 sm:p-6 rounded-lg text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-purple-100 text-xs sm:text-sm">En cours</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {stats.find(s => s.id === 'en-cours')?.count || 0}
              </p>
            </div>
            <i className="ri-time-line text-2xl sm:text-3xl text-purple-200 hidden sm:block"></i>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 sm:p-6 rounded-lg text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-orange-100 text-xs sm:text-sm">RDV aujourd'hui</p>
              <p className="text-2xl sm:text-3xl font-bold">{rdvToday.length}</p>
            </div>
            <i className="ri-calendar-check-line text-2xl sm:text-3xl text-orange-200 hidden sm:block"></i>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Clients récents - Mobile optimisé */}
        <div className="mobile-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Clients récents</h2>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
              <span className="text-xs text-green-600">Auto</span>
            </div>
          </div>
          <div className="space-y-3 max-h-80 sm:max-h-96 overflow-y-auto">
            {recentClients.length > 0 ? recentClients.map((client) => {
              const status = statuses.find(s => s.id === client.status);
              const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
              
              return (
                <div key={client.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors mobile-card-hover">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                      {client.first_name} {client.last_name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{client.phone}</p>
                    {currentUser.role === 'admin' && (
                      <p className="text-xs text-gray-500 mt-1 truncate">Agent: {agent?.name || 'Non assigné'}</p>
                    )}
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <span 
                      className="inline-block px-2 py-1 text-xs font-medium text-white rounded-full"
                      style={{ backgroundColor: status?.color || '#6B7280' }}
                    >
                      {status?.label || 'Statut inconnu'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(client.updated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-user-line text-4xl mb-2 block"></i>
                <p className="text-sm">Aucun client récent</p>
                <p className="text-xs">Les nouveaux clients apparaîtront ici</p>
              </div>
            )}
          </div>
        </div>

        {/* RDV du jour - Mobile optimisé */}
        <div className="mobile-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              RDV aujourd'hui ({rdvToday.length})
            </h2>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></div>
              <span className="text-xs text-red-600">Urgent</span>
            </div>
          </div>
          <div className="space-y-3 max-h-80 sm:max-h-96 overflow-y-auto">
            {rdvToday.length > 0 ? rdvToday
              .sort((a, b) => (a.rdv_time || '').localeCompare(b.rdv_time || ''))
              .map((client) => {
                const agent = users.find(u => u.id === client.assigned_agent) || client.assigned_user;
                
                return (
                  <div key={client.id} className="bg-red-50 border border-red-200 rounded-lg p-3 mobile-card-hover">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{client.phone}</p>
                        <div className="flex items-center mt-2">
                          <i className="ri-time-line text-red-600 mr-1"></i>
                          <span className="text-sm font-medium text-red-600">
                            {client.rdv_time || 'Heure non définie'}
                          </span>
                        </div>
                        {currentUser.role === 'admin' && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            Agent: {agent?.name}
                          </p>
                        )}
                      </div>
                      <button className="touch-target text-blue-600 hover:text-blue-900 ml-2 flex-shrink-0">
                        <i className="ri-eye-line"></i>
                      </button>
                    </div>
                  </div>
                );
              }) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-calendar-line text-4xl mb-2 block"></i>
                <p className="text-sm">Aucun RDV aujourd'hui</p>
                <p className="text-xs">Profitez de votre journée !</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Graphique de progression mobile */}
      <div className="mt-6 mobile-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Répartition par statut</h2>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse mr-2"></div>
            <span className="text-xs text-purple-600">Auto</span>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
          {stats.map((stat) => {
            const maxCount = Math.max(1, Math.max(...stats.map(s => s.count)));
            const percentage = totalClients > 0 ? (stat.count / totalClients) * 100 : 0;
            
            return (
              <div key={stat.id} className="text-center">
                <div 
                  className="w-full rounded-lg mb-2 flex items-end justify-center text-white font-bold text-xs sm:text-sm transition-all duration-500 hover:scale-105 mobile-card-hover"
                  style={{ 
                    backgroundColor: stat.color,
                    height: `${Math.max(30, (stat.count / maxCount) * 80)}px`
                  }}
                >
                  {stat.count}
                </div>
                <p className="text-xs text-gray-600 font-medium truncate">{stat.label}</p>
                <p className="text-xs text-gray-500">{percentage.toFixed(0)}%</p>
              </div>
            );
          })}
        </div>
        
        {totalClients === 0 && (
          <div className="text-center py-12 text-gray-500">
            <i className="ri-bar-chart-line text-6xl mb-4 block"></i>
            <p className="text-base sm:text-lg font-medium">Aucune donnée</p>
            <p className="text-sm">Ajoutez des clients pour voir les statistiques</p>
          </div>
        )}
      </div>
    </div>
  );
}
